import { classes } from "portal/theme/theme";
import {
  getAxisTicks,
  getXAxisLabelPosition,
  getXAxisTickLabelY,
  ShootTimeXScale,
} from "./layout";
import { getMaxRadiusMmFromBands, ShootTimeBand } from "portal/utils/shootTime";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useMemo } from "react";

/** X-axis ticks and the plant-radius title along the bottom of the plot. */
export interface ShootTimeChartXAxisProps {
  bands: ShootTimeBand[];
  chartHeight: number;
  plotLeft: number;
  plotWidth: number;
  xScale: ShootTimeXScale;
  className?: string;
}

export const ShootTimeChartXAxis: FunctionComponent<
  ShootTimeChartXAxisProps
> = ({ bands, chartHeight, plotLeft, plotWidth, xScale, className }) => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { tickColor },
    },
  } = useTheme();
  const tickLabelY = getXAxisTickLabelY(chartHeight);
  const { x: labelX, y: labelY } = getXAxisLabelPosition(
    chartHeight,
    plotLeft,
    plotWidth
  );

  const maxRadiusMm = useMemo(() => getMaxRadiusMmFromBands(bands), [bands]);
  const ticks = useMemo(() => getAxisTicks(maxRadiusMm), [maxRadiusMm]);

  return (
    <>
      {ticks.map((tick) => {
        const isMaxTick = tick === maxRadiusMm;
        return (
          <text
            key={`x-axis-${tick}`}
            x={xScale(tick)}
            y={tickLabelY}
            textAnchor={isMaxTick ? "end" : "middle"}
            fill={tickColor}
            className={classes("text-xs", className)}
          >
            {isMaxTick ? t("utils.units.valuePlus", { value: tick }) : tick}
          </text>
        );
      })}
      <text
        x={labelX}
        y={labelY}
        textAnchor="end"
        dominantBaseline="auto"
        fill={tickColor}
        className={classes("text-xs", className)}
      >
        {t("views.categoryTemplates.shootTime.xAxisLabel")}
      </text>
    </>
  );
};
